/**
 * Base PDF parser for Belgian DIV registration documents.
 * Reads PDF text via pdf.js, extracts labelled fields, and builds the form
 * Format-specific parsers (portima, inmotiv, standardDiv) only supply
 */
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// Worker must match the installed pdfjs-dist version (copied to public/ on npm install).
const workerPath = `${process.env.BASE_URL || '/'}pdf.worker.min.js`.replace(
  /\/+/g,
  '/'
);
pdfjsLib.GlobalWorkerOptions.workerSrc =
  typeof window !== 'undefined'
    ? new URL(workerPath, window.location.origin).href
    : workerPath;

/** Shape expected by Forms.vue when pre-filling from an uploaded PDF. */
export interface DivRegistrationResult {
  vehicle: {
    licensePlate: string;
    brand: string;
    model: string;
    code: string;
  };
  customer: {
    name: string;
    address: { address: string; postalCode: string; town: string };
  };
  insurance: { fsma: string; insurerName: string; registrationDate: string };
}

/** Per-page text: full page plus left/right columns for two-column layouts (inMotiv). */
export type PdfPageContent = { full: string; left: string; right: string };

type PdfItem = { str: string; x: number; y: number };

// Section headers found across PORTIMA, inMotiv and standaard DIV registration documents.
// Used to slice the flat text into named blocks before field lookup.
const SECTIONS = [
  'REGISTRATIEGEGEVENS',
  'TRANSACTIE',
  'VERZEKERINGNEMER',
  'EIGENAAR',
  'GEGEVENS VOERTUIG',
  'VOERTUIG',
  'GEGEVENS VERZEKERINGSMAATSCHAPPIJ',
  'GEGEVENS VERZEKERINGMAATSCHAPPIJ',
  'VERZEKERINGSBEDRIJF',
  'VERZEKERINGSINSTELLING'
];

const POSTCODE_RE = /\b(\d{4})\s+([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ -]+)/;

/**
 * Reconstructs reading-order lines from scattered PDF text fragments.
 * Items on the same y-coordinate (within 4pt) belong to one line.
 */
function buildLines(items: PdfItem[]): string {
  items.sort((a, b) => {
    const yDiff = b.y - a.y;
    return Math.abs(yDiff) > 4 ? yDiff : a.x - b.x;
  });
  const lines: string[] = [];
  let current: string[] = [];
  let lastY: number | null = null;
  items.forEach(item => {
    if (lastY !== null && Math.abs(item.y - lastY) > 4) {
      lines.push(current.join(' '));
      current = [];
    }
    current.push(item.str);
    lastY = item.y;
  });
  if (current.length) lines.push(current.join(' '));
  return lines.join('\n');
}

/** Extracts text from every page, splitting each page into left/right columns. */
export async function readPdf(file: File): Promise<PdfPageContent[]> {
  const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() })
    .promise;
  const pages: PdfPageContent[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const content: any = await (await pdf.getPage(i)).getTextContent();
    const items: PdfItem[] = content.items
      .filter((item: any) => item.str?.trim())
      .map((item: any) => ({
        str: item.str.trim(),
        x: item.transform[4],
        y: item.transform[5]
      }));
    // inMotiv/WEBDIV prints vehicle data left, owner data right — split near the page centre.
    const splitX = items.reduce((max, item) => Math.max(max, item.x), 0) * 0.48;
    pages.push({
      full: buildLines(items),
      left: buildLines(items.filter(item => item.x < splitX)),
      right: buildLines(items.filter(item => item.x >= splitX))
    });
  }
  return pages;
}

export function joinPages(
  pages: PdfPageContent[],
  column: 'full' | 'left' | 'right' = 'full'
): string {
  return pages.map(page => page[column]).join('\n');
}

/** Stops at the next field label when PDF text runs multiple values onto one line. */
function trimValue(value: string): string {
  return value
    .split(
      /\s+(?=(?:Naam|Plaatnummer|Nummerplaat|Adres|Begindatum|Verzekering(?:s)?maatschappij)\s*:)/i
    )[0]
    .trim();
}

/**
 * Reads a labelled field from DIV text. Handles three common PDF layouts:
 * label on its own line (value on next line), label and value on same line,
 * or "Label: value" inline.
 */
export function fieldValue(text: string, label: string): string {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const lines = text.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (new RegExp(`^${escaped}$`, 'i').test(line)) {
      return trimValue((lines[i + 1] || '').trim());
    }
    if (new RegExp(`^${escaped}(?:\\s|:)`, 'i').test(line)) {
      const sameLine = line
        .replace(new RegExp(`^${escaped}\\s*:?\\s*`, 'i'), '')
        .trim();
      if (sameLine) return trimValue(sameLine);
      const next = trimValue((lines[i + 1] || '').trim());
      if (next) return next;
    }
  }

  const match = text.match(new RegExp(`${escaped}\\s*:?\\s+([^\\n]+)`, 'i'));
  return match ? trimValue(match[1].trim()) : '';
}

/** Tries each label inside named sections first, then falls back to the full text. */
function pick(
  text: string,
  labels: string[],
  blocks: Record<string, string> = {},
  blockNames: string[] = []
): string {
  for (const name of blockNames) {
    const block = blocks[name];
    if (!block) continue;
    for (const label of labels) {
      const value = fieldValue(block, label);
      if (value) return value;
    }
  }
  for (const label of labels) {
    const value = fieldValue(text, label);
    if (value) return value;
  }
  return '';
}

/** Returns the text between a section header and the next section header. */
function extractSection(
  text: string,
  startLabel: string,
  endLabels: string[]
): string {
  const start = text.search(new RegExp(`\\b${startLabel}\\b`, 'i'));
  if (start < 0) return '';

  let end = text.length;
  const tail = text.slice(start + startLabel.length);
  endLabels.forEach(endLabel => {
    const idx = tail.search(new RegExp(`\\b${endLabel}\\b`, 'i'));
    if (idx >= 0) end = Math.min(end, start + startLabel.length + idx);
  });
  return text.slice(start, end);
}

/** Splits flat PDF text into named section blocks (VOERTUIG, EIGENAAR, …). */
function getBlocks(text: string): Record<string, string> {
  const positions: { start: number; name: string }[] = [];
  SECTIONS.forEach(name => {
    const match = text.match(new RegExp(`\\b${name}\\b`, 'i'));
    if (match?.index !== undefined)
      positions.push({ start: match.index, name });
  });
  positions.sort((a, b) => a.start - b.start);
  if (!positions.length) return { GENERAL: text };

  const blocks: Record<string, string> = {
    GENERAL: text.slice(0, positions[0].start)
  };
  positions.forEach((pos, index) => {
    const end = positions[index + 1]?.start ?? text.length;
    blocks[pos.name] = text.slice(pos.start, end);
  });
  return blocks;
}

/** Extracts policyholder street/postcode/town from the policyholder section only. */
function parseAddress(section: string) {
  const empty = { address: '', postalCode: '', town: '' };
  if (!section) return empty;

  const street = pick(section, [
    'Straat, nummer en bus',
    'Straat nr.',
    'Straat',
    'Adres',
    'Plaats'
  ]);
  const pcCity = pick(section, [
    'Postcode en gemeente',
    'Postcode en stad',
    'Postcode'
  ]);

  if (pcCity) {
    const match = pcCity.match(/^(\d{4})\s+(.+)$/);
    if (match) {
      return {
        address: street.replace(/^nr\.?\s*:?\s*/i, '').trim(),
        postalCode: match[1],
        town: match[2].trim()
      };
    }
  }

  const match = section.match(POSTCODE_RE);
  if (match && street) {
    return {
      address: street.replace(/^nr\.?\s*:?\s*/i, '').trim(),
      postalCode: match[1],
      town: match[2].trim()
    };
  }
  return empty;
}

/** Splits brandModel into brand + model. */
function parseBrandModel(brandType: string) {
  const cleaned = brandType.replace(/\([^)]*\)/g, '').trim();
  const parts = cleaned.split(/\s+/);
  return { brand: parts[0] || '', model: parts.slice(1).join(' ') || '' };
}

/** Maps DIV vehicle category to the letter code used on the insurance certificate form. */
function vehicleCode(vehicleNature: string, divCategory: string): string {
  const combined = `${vehicleNature} ${divCategory}`.toUpperCase();
  if (/MOTOR|L3/.test(combined)) return 'B';
  if (/BROM|L1|L2/.test(combined)) return 'D';
  if (/\bCT\b|VRACHT|N1|N2|N3|LICHTE|TREKKER|TREK/.test(combined)) return 'C';
  if (/AANHANG|OPLEG|O[1-4]/.test(combined)) return 'F';
  if (/BUS|M2|M3/.test(combined)) return 'E';
  return 'A';
}

/** Normalises Belgian (DD/MM/YYYY) and ISO (YYYY-MM-DD) dates to DD/MM/YYYY. */
function parseDate(...values: string[]): string {
  for (const raw of values) {
    if (!raw) continue;
    const belgian = raw.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (belgian) {
      return `${belgian[1].padStart(2, '0')}/${belgian[2].padStart(2, '0')}/${
        belgian[3]
      }`;
    }
    const iso = raw.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  }
  return '';
}

/**
 * Field-mapping config passed in by each format-specific parser.
 * Each parser only declares which labels/sections to read; buildResult does the rest.
 */
export type ParseContext = {
  text: string;
  blocks: Record<string, string>;
  policyholderSection: string;
  vehicleBlocks: string[];
  plateLabels: string[];
  plateBlocks: string[];
  plateSection?: string;
  brandLabels: string[];
  natureLabels: string[];
  categoryLabels: string[];
  insurerSection: string;
  insurerLabels: string[];
  insurerBlocks: string[];
  insurerRegex?: RegExp;
  dateLabels: string[];
  dateBlocks: string[];
  dateExtra?: string[];
  /** Full-page text for vehicle fields when labels/values sit in separate columns (inMotiv). */
  vehicleSource?: string;
};

/** Reads all fields from a ParseContext and returns the form pre-fill payload. */
export function buildResult(ctx: ParseContext): DivRegistrationResult {
  const { text, blocks, policyholderSection, vehicleBlocks } = ctx;
  const vehicleText =
    vehicleBlocks.map(name => blocks[name]).find(Boolean) || text;
  const vehicleBlockMap = ctx.vehicleSource
    ? getBlocks(ctx.vehicleSource)
    : blocks;
  const vehicleSearchText = ctx.vehicleSource || vehicleText;

  const name = pick(policyholderSection, ['Naam en voornaam', 'Naam']);

  const plateRaw =
    (ctx.plateSection
      ? fieldValue(ctx.plateSection, ctx.plateLabels[0])
      : '') || pick(text, ctx.plateLabels, blocks, ctx.plateBlocks);

  const brandType = pick(
    vehicleSearchText,
    ctx.brandLabels,
    vehicleBlockMap,
    vehicleBlocks
  );
  const vehicleNature = pick(
    vehicleSearchText,
    ctx.natureLabels,
    vehicleBlockMap,
    vehicleBlocks
  );
  const divCategory = pick(
    vehicleSearchText,
    ctx.categoryLabels,
    vehicleBlockMap,
    vehicleBlocks
  );

  const insurerRaw =
    pick(ctx.insurerSection, ctx.insurerLabels) ||
    pick(text, ctx.insurerLabels, blocks, ctx.insurerBlocks) ||
    (ctx.insurerRegex ? (text.match(ctx.insurerRegex) || [])[1] : '') ||
    '';

  const registrationDate = parseDate(
    ...(ctx.dateExtra || []),
    pick(text, ctx.dateLabels, blocks, ctx.dateBlocks)
  );
  const { brand, model } = parseBrandModel(brandType);
  // FSMA may appear as "TVM Belgium (02796)" or as a bare number "58".
  const fsmaMatch = insurerRaw.match(/\((\d+)\)/);
  const fsma = fsmaMatch
    ? fsmaMatch[1].replace(/\D/g, '')
    : insurerRaw.replace(/\D/g, '');
  const plateMatch = plateRaw.match(/\b([0-9][A-Z][A-Z0-9]{4,6})\b/i);

  const result: DivRegistrationResult = {
    vehicle: {
      licensePlate: plateMatch
        ? plateMatch[1].toUpperCase()
        : plateRaw.trim().split(/\s+/)[0] || '',
      brand,
      model,
      code: vehicleCode(vehicleNature, divCategory)
    },
    customer: {
      name: name.trim(),
      address: parseAddress(policyholderSection)
    },
    insurance: {
      fsma,
      insurerName: insurerRaw.replace(/\(\d+\)/, '').trim(),
      registrationDate
    }
  };

  if (!result.vehicle.licensePlate && !result.customer.name) {
    throw new Error('Geen inschrijvingsgegevens gevonden in dit document.');
  }
  return result;
}

export { extractSection, getBlocks };
